import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Multi-region logical replication (part 117).
 *
 * The page reads the live catalog through the API: pg_publication and pg_publication_tables for
 * the published table set, pg_stat_subscription and pg_subscription_rel for the subscriber's
 * worker and initial-copy progress, and pg_replication_slots for WAL retention. Lag is a real LSN
 * subtraction, not a heartbeat. A subscription the catalog does not contain is shown as
 * "ยังไม่สร้างบนเครื่องปลายทาง", never as a green zero.
 *
 * This page is not routed from App.tsx in this part, and the API mount is intentionally not called
 * from server.go. Both are one-line wiring changes for the operator who turns the feature on.
 */

type ObservedState =
  | 'draft'
  | 'creating'
  | 'copying'
  | 'streaming'
  | 'lagging'
  | 'disabled'
  | 'error'
  | 'unknown'
  | 'dropped'

type SlotRisk = 'ok' | 'watch' | 'high' | 'critical' | 'unknown'

interface TableRef {
  schema: string
  name: string
}

interface Finding {
  code: string
  message: string
}

interface TableDrift {
  in_sync: boolean
  missing: TableRef[]
  extra: TableRef[]
  live_all_tables: boolean
  missing_all_tables: boolean
}

interface PublicationRow {
  id: string
  name_th: string
  publication_name: string
  source_database: string
  all_tables: boolean
  tables: TableRef[]
  publish_insert: boolean
  publish_update: boolean
  publish_delete: boolean
  publish_truncate: boolean
  publish_via_partition_root: boolean
  status: 'active' | 'paused' | 'retired'
  notes: string
  live_exists: boolean
  live_all_tables: boolean
  live_owner: string
  live_tables: TableRef[]
  table_count: number
  drift: TableDrift
}

interface Lag {
  publisher_lsn: string
  received_lsn: string
  applied_lsn: string
  received_bytes: number
  applied_bytes: number
  known: boolean
  received_known: boolean
  applied_known: boolean
  seconds: number
  seconds_known: boolean
  rate_bps: number
  eta_known: boolean
  eta: number
}

interface SlotAssessment {
  risk: SlotRisk
  label_th: string
  tone: string
  action_th: string
  retained_bytes: number
  retention_known: boolean
  will_invalidate: boolean
  active: boolean
  wal_status: string
  findings: Finding[]
}

interface SubscriptionRow {
  id: string
  publication_id: string
  publication_name: string
  name_th: string
  subscription_name: string
  slot_name: string
  subscriber_region: string
  host: string
  port: number
  database_name: string
  enabled: boolean
  conflict_policy: 'halt' | 'retry' | 'skip_transaction' | 'pause_and_review'
  max_lag_seconds: number
  state: ObservedState
  last_error: string
  created_at: string
  updated_at: string
  live_exists: boolean
  live_enabled: boolean
  live_worker_pid: number
  live_received_lsn: string
  live_latest_end_lsn: string
  live_applied_lsn: string
  live_tables_total: number
  live_tables_ready: number
  live_last_receipt_at?: string
  live_slot_active: boolean
  live_slot_retained_bytes: number
  live_slot_wal_status: string
  live_error?: string
  observed_state: ObservedState
  observed_label: string
  tone: string
  lag: Lag
  slot: SlotAssessment
  findings: Finding[]
  ready_for_cutover: boolean
}

interface SlotRow {
  name: string
  active: boolean
  temporary: boolean
  restart_lsn: string
  confirmed_flush_lsn: string
  wal_status: string
  safe_wal_bytes: number
  inactive_since?: string
  retained_bytes: number
  retention_known: boolean
  max_slot_wal_keep_bytes: number
  assessment: SlotAssessment
}

interface Report {
  generated_at: string
  current_lsn: string
  live_error?: string
  publications: PublicationRow[]
  subscriptions: SubscriptionRow[]
  slots: SlotRow[]
  summary: {
    subscriptions: number
    streaming: number
    lagging: number
    copying: number
    error: number
    disabled: number
    draft: number
    unknown: number
    slots: number
    critical_slots: number
  }
  notes: string[]
}

interface LagRow {
  id: string
  subscription_id: string
  captured_at: string
  state: ObservedState
  lag_known: boolean
  lag_bytes: number
  applied_lag_bytes: number
  lag_seconds: number
  seconds_known: boolean
  slot_risk: SlotRisk
}

const REPORT_KEY = ['logical-replication', 'report'] as const

export function LogicalReplication() {
  const qc = useQueryClient()
  const { me } = useAuth()
  const canManage = me?.role === 'owner' || me?.role === 'admin'

  const [error, setError] = useState('')
  const [publicationID, setPublicationID] = useState('')
  const [nameTH, setNameTH] = useState('')
  const [subscriptionName, setSubscriptionName] = useState('')
  const [slotName, setSlotName] = useState('')
  const [region, setRegion] = useState('')
  const [host, setHost] = useState('')
  const [port, setPort] = useState('5432')
  const [databaseName, setDatabaseName] = useState('auditai')
  const [conflictPolicy, setConflictPolicy] = useState<'halt' | 'retry' | 'skip_transaction' | 'pause_and_review'>('halt')
  const [maxLagSeconds, setMaxLagSeconds] = useState('300')

  const report = useQuery({
    queryKey: REPORT_KEY,
    queryFn: () => api<Report>('/v1/logical-replication/lag'),
    refetchInterval: 30_000,
  })

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: REPORT_KEY })
  }

  const createSubscription = useMutation({
    mutationFn: () =>
      apiWrite<SubscriptionRow>('/v1/logical-replication/subscriptions', 'POST', {
        publication_id: publicationID,
        name_th: nameTH.trim(),
        subscription_name: subscriptionName.trim(),
        slot_name: slotName.trim(),
        subscriber_region: region.trim(),
        host: host.trim(),
        port: Number(port) || 5432,
        database_name: databaseName.trim(),
        enabled: true,
        conflict_policy: conflictPolicy,
        max_lag_seconds: Number(maxLagSeconds) || 300,
      }),
    onSuccess: () => {
      setError('')
      setNameTH('')
      setSubscriptionName('')
      setSlotName('')
      refresh()
    },
    onError: (err) => setError(errorText(err, 'เพิ่ม subscription ไม่สำเร็จ')),
  })

  const captureLag = useMutation({
    mutationFn: (subscriptionID?: string) =>
      apiWrite<LagRow[]>('/v1/logical-replication/lag', 'POST', subscriptionID ? { subscription_id: subscriptionID } : {}),
    onSuccess: () => {
      setError('')
      refresh()
    },
    onError: (err) => setError(errorText(err, 'บันทึกผลวัดความล่าช้าไม่สำเร็จ')),
  })

  const dropSubscription = useMutation({
    mutationFn: (subscriptionID: string) =>
      apiWrite<SubscriptionRow>(`/v1/logical-replication/subscriptions/${subscriptionID}`, 'DELETE'),
    onSuccess: () => {
      setError('')
      refresh()
    },
    onError: (err) => setError(errorText(err, 'ยกเลิก subscription ไม่สำเร็จ')),
  })

  const data = report.data
  const publications = data?.publications ?? []
  const subscriptions = data?.subscriptions ?? []
  const slots = data?.slots ?? []
  const summary = data?.summary

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setError('')
    if (!publicationID) {
      setError('เลือก publication ก่อนบันทึก subscription')
      return
    }
    createSubscription.mutate()
  }

  const confirmDrop = (sub: SubscriptionRow) => {
    if (!window.confirm(`ยกเลิก subscription ${sub.subscription_name}? สถานะจะถูกทำเครื่องหมายว่ายกเลิก และต้องรัน DROP SUBSCRIPTION บนเครื่องปลายทางเพื่อคืนสล็อต`)) {
      return
    }
    dropSubscription.mutate(sub.id)
  }

  return (
    <>
      <PageHead
        title="การจำลองข้อมูลหลายภูมิภาค"
        sub="ตรวจ publication, subscription และสล็อตจากแค็ตตาล็อกของ PostgreSQL จริง ความล่าช้าคำนวณจาก LSN ไม่ใช่สัญญาณ heartbeat"
        actions={
          <button
            className="btn btn-sm"
            onClick={() => captureLag.mutate(undefined)}
            disabled={captureLag.isPending || subscriptions.length === 0}
          >
            {captureLag.isPending ? 'กำลังวัด…' : 'วัดและบันทึกผลทั้งหมด'}
          </button>
        }
      />

      {error ? (
        <div className="card card-pad" style={{ borderColor: 'var(--color-critical)' }}>
          <span className="sev sev-critical">
            <i className="sev-dot" />
            {error}
          </span>
        </div>
      ) : null}

      {data?.live_error ? (
        <div className="card card-pad" style={{ borderColor: 'var(--color-medium)' }}>
          <span className="sev sev-medium">
            <i className="sev-dot" />
            อ่านข้อมูลสดได้ไม่ครบ: {data.live_error}
          </span>
        </div>
      ) : null}

      <div className="grid-4" style={{ marginBottom: 16 }}>
        <Stat label="subscription ทั้งหมด" value={number(summary?.subscriptions ?? subscriptions.length)} unit="รายการ" foot="รวมทุกสถานะที่กำหนดไว้" />
        <Stat label="สตรีมปกติ" value={number(summary?.streaming ?? 0)} unit="รายการ" foot="worker ทำงานและอยู่ในงบความล่าช้า" />
        <Stat
          label="ต้องตรวจสอบ"
          value={number((summary?.lagging ?? 0) + (summary?.error ?? 0))}
          unit="รายการ"
          foot="ล่าช้าเกินงบหรือ worker หยุด"
        />
        <Stat
          label="สล็อตเสี่ยงวิกฤต"
          value={number(summary?.critical_slots ?? 0)}
          unit="สล็อต"
          foot="WAL อาจถูกตัดหรือถูกตัดแล้ว"
        />
      </div>

      <div className="stack" style={{ gap: 16 }}>
        <div className="card">
          <div className="card-pad" style={{ paddingBottom: 8 }}>
            <div className="row">
              <span className="card-label">publication และชุดตาราง</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                เทียบชุดตารางที่กำหนดกับ pg_publication_tables จริง
              </span>
            </div>
          </div>
          {report.isLoading ? (
            <div className="card-pad stack" style={{ gap: 8 }}>
              <Skel h={14} />
              <Skel h={14} w="70%" />
            </div>
          ) : publications.length === 0 ? (
            <div className="card-pad">
              <Empty
                title="ยังไม่มี publication"
                body="เพิ่ม publication ในฐานข้อมูลผู้เผยแพร่ก่อน แล้วกำหนดชุดตารางที่ต้องการส่งไปยังภูมิภาคปลายทาง"
              />
            </div>
          ) : (
            <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>publication</th>
                    <th>ฐานข้อมูลต้นทาง</th>
                    <th>ชุดตาราง</th>
                    <th>สถานะจริง</th>
                    <th>ความสอดคล้อง</th>
                    <th>การดำเนินการ</th>
                  </tr>
                </thead>
                <tbody>
                  {publications.map((pub) => (
                    <tr key={pub.id}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{pub.name_th}</div>
                        <div className="mono dim">{pub.publication_name}</div>
                      </td>
                      <td className="mono dim">{pub.source_database || 'ฐานข้อมูลปัจจุบัน'}</td>
                      <td>
                        {pub.all_tables ? (
                          <span className="chip">ทุกตารางในฐานข้อมูล</span>
                        ) : (
                          <span className="chip">{number(pub.table_count)} ตาราง</span>
                        )}
                        {!pub.all_tables && pub.tables.length > 0 ? (
                          <div className="dim mono" style={{ fontSize: 11.5, marginTop: 4 }}>
                            {pub.tables.slice(0, 3).map((table) => `${table.schema}.${table.name}`).join(', ')}
                            {pub.tables.length > 3 ? ` และอีก ${pub.tables.length - 3}` : ''}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        {pub.live_exists ? (
                          <span className="sev sev-low">
                            <i className="sev-dot" />
                            พบใน PostgreSQL
                          </span>
                        ) : (
                          <span className="sev sev-critical">
                            <i className="sev-dot" />
                            ไม่พบ publication นี้
                          </span>
                        )}
                        {pub.live_owner ? <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>เจ้าของ {pub.live_owner}</div> : null}
                      </td>
                      <td>
                        {pub.drift.in_sync ? (
                          <span className="sev sev-low">
                            <i className="sev-dot" />
                            ตรงกัน
                          </span>
                        ) : (
                          <span className="sev sev-high">
                            <i className="sev-dot" />
                            ไม่ตรงกัน
                          </span>
                        )}
                        {pub.drift.missing.length > 0 ? (
                          <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                            ขาด {pub.drift.missing.map((table) => `${table.schema}.${table.name}`).join(', ')}
                          </div>
                        ) : null}
                        {pub.drift.extra.length > 0 ? (
                          <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                            เกิน {pub.drift.extra.length} ตาราง
                          </div>
                        ) : null}
                        {pub.drift.missing_all_tables ? (
                          <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                            กำหนดให้เผยแพร่ทุกตาราง แต่ PostgreSQL ไม่ได้ตั้งค่า FOR ALL TABLES
                          </div>
                        ) : null}
                      </td>
                      <td className="dim">
                        {pub.publish_insert ? 'INSERT ' : ''}
                        {pub.publish_update ? 'UPDATE ' : ''}
                        {pub.publish_delete ? 'DELETE ' : ''}
                        {pub.publish_truncate ? 'TRUNCATE' : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="grid-main">
          <div className="stack" style={{ gap: 16 }}>
            <div className="card">
              <div className="card-pad" style={{ paddingBottom: 8 }}>
                <div className="row">
                  <span className="card-label">subscription ข้ามภูมิภาค</span>
                  <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                    LSN ปัจจุบัน {data?.current_lsn || 'ยังอ่านไม่ได้'}
                  </span>
                </div>
              </div>
              {report.isLoading ? (
                <div className="card-pad stack" style={{ gap: 8 }}>
                  <Skel h={14} />
                  <Skel h={14} w="60%" />
                </div>
              ) : subscriptions.length === 0 ? (
                <div className="card-pad">
                  <Empty
                    title="ยังไม่มี subscription"
                    body="ลงทะเบียน subscription ทางขวา แล้วรัน CREATE SUBSCRIPTION บนเครื่องปลายทาง หรือใช้ scripts/logical-replication-drill.sh เพื่อพิสูจน์การส่งข้อมูลจริง"
                  />
                </div>
              ) : (
                <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>subscription</th>
                        <th>สถานะที่วัดได้</th>
                        <th>ข้อมูลค้าง</th>
                        <th>ตาราง</th>
                        <th>สล็อต</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map((sub) => (
                        <tr key={sub.id}>
                          <td>
                            <div style={{ fontWeight: 500 }}>{sub.name_th}</div>
                            <div className="mono dim">{sub.subscription_name}</div>
                            <div className="dim" style={{ fontSize: 11.5, marginTop: 3 }}>
                              {sub.subscriber_region} · {sub.host}:{sub.port}/{sub.database_name}
                            </div>
                          </td>
                          <td>
                            <span className={sevClass(stateTone(sub.observed_state))}>
                              <i className="sev-dot" />
                              {sub.observed_label}
                            </span>
                            <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                              worker {sub.live_worker_pid > 0 ? `PID ${sub.live_worker_pid}` : 'ไม่ทำงาน'}
                            </div>
                            {sub.findings.length > 0 ? (
                              <div className="dim" style={{ fontSize: 11.5, marginTop: 3 }}>
                                {sub.findings[0].message}
                              </div>
                            ) : null}
                          </td>
                          <td className="tnum">
                            {sub.lag.known ? bytesText(sub.lag.applied_bytes || sub.lag.received_bytes) : 'ยังวัดไม่ได้'}
                            <div className="dim" style={{ fontSize: 11.5 }}>
                              {sub.lag.seconds_known ? `${number(sub.lag.seconds)} วินาที` : 'ยังประมาณเวลาไม่ได้'}
                            </div>
                          </td>
                          <td className="tnum">
                            {sub.live_tables_total > 0 ? `${number(sub.live_tables_ready)}/${number(sub.live_tables_total)}` : '—'}
                            <div className="dim" style={{ fontSize: 11.5 }}>
                              {sub.ready_for_cutover ? 'พร้อมตัดอ่าน' : 'ยังไม่พร้อมตัดอ่าน'}
                            </div>
                          </td>
                          <td>
                            <span className={sevClass(slotTone(sub.slot.risk))}>
                              <i className="sev-dot" />
                              {sub.slot.label_th}
                            </span>
                            <div className="dim mono" style={{ fontSize: 11.5, marginTop: 4 }}>
                              {sub.slot_name}
                            </div>
                            <div className="dim" style={{ fontSize: 11.5 }}>
                              {sub.slot.retention_known ? bytesText(sub.slot.retained_bytes) : 'ยังอ่าน retention ไม่ได้'}
                            </div>
                          </td>
                          <td>
                            <div className="row" style={{ gap: 6 }}>
                              <button
                                className="btn btn-sm btn-ghost"
                                onClick={() => captureLag.mutate(sub.id)}
                                disabled={captureLag.isPending}
                              >
                                วัด
                              </button>
                              {canManage && sub.state !== 'dropped' ? (
                                <button
                                  className="btn btn-sm btn-ghost"
                                  onClick={() => confirmDrop(sub)}
                                  disabled={dropSubscription.isPending}
                                >
                                  ยกเลิก
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="card">
              <div className="card-pad" style={{ paddingBottom: 8 }}>
                <span className="card-label">สล็อตและ WAL ที่ถูกหน่วง</span>
              </div>
              {slots.length === 0 ? (
                <div className="card-pad">
                  <Empty title="ยังไม่มี logical slot" body="สล็อตจะถูกสร้างเมื่อมี CREATE SUBSCRIPTION บนเครื่องปลายทาง และจะหายเมื่อรัน DROP SUBSCRIPTION" />
                </div>
              ) : (
                <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>สล็อต</th>
                        <th>ผู้ใช้</th>
                        <th>WAL ที่หน่วง</th>
                        <th>สถานะ WAL</th>
                        <th>ความเสี่ยง</th>
                        <th>คำแนะนำ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {slots.map((slot) => (
                        <tr key={slot.name}>
                          <td className="mono">{slot.name}</td>
                          <td>
                            {slot.active ? (
                              <span className="sev sev-low">
                                <i className="sev-dot" />
                                ใช้งานอยู่
                              </span>
                            ) : (
                              <span className="sev sev-high">
                                <i className="sev-dot" />
                                ไม่ได้ใช้งาน
                              </span>
                            )}
                            {slot.temporary ? <div className="dim" style={{ fontSize: 11.5 }}>สล็อตชั่วคราว</div> : null}
                          </td>
                          <td className="tnum">
                            {slot.retention_known ? bytesText(slot.retained_bytes) : 'ยังวัดไม่ได้'}
                            {slot.inactive_since ? (
                              <div className="dim" style={{ fontSize: 11.5 }}>ว่างตั้งแต่ {thaiDateTime(slot.inactive_since)}</div>
                            ) : null}
                          </td>
                          <td className="mono dim">{slot.wal_status || '—'}</td>
                          <td>
                            <span className={sevClass(slotTone(slot.assessment.risk))}>
                              <i className="sev-dot" />
                              {slot.assessment.label_th}
                            </span>
                            {slot.assessment.will_invalidate ? (
                              <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                                PostgreSQL จะทำให้สล็อตเสียหาย
                              </div>
                            ) : null}
                          </td>
                          <td className="dim" style={{ fontSize: 12 }}>
                            {slot.assessment.action_th}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="stack" style={{ gap: 16 }}>
            {canManage ? (
              <form className="card card-pad" onSubmit={submit}>
                <div className="row" style={{ marginBottom: 4 }}>
                  <span className="card-label">ลงทะเบียน subscription</span>
                  <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                    เฉพาะเจ้าของและผู้ดูแลระบบ
                  </span>
                </div>
                <label className="stack" style={{ gap: 6 }}>
                  <span className="dim" style={{ fontSize: 11.5 }}>publication</span>
                  <select className="input" value={publicationID} onChange={(e) => setPublicationID(e.target.value)} required>
                    <option value="">เลือก publication</option>
                    {publications.map((pub) => (
                      <option key={pub.id} value={pub.id}>
                        {pub.name_th} ({pub.publication_name})
                      </option>
                    ))}
                  </select>
                </label>
                <label className="stack" style={{ gap: 6 }}>
                  <span className="dim" style={{ fontSize: 11.5 }}>ชื่อ subscription</span>
                  <input className="input" value={nameTH} onChange={(e) => setNameTH(e.target.value)} placeholder="ศูนย์สำรอง สิงคโปร์" required />
                </label>
                <div className="grid-2" style={{ gap: 12 }}>
                  <label className="stack" style={{ gap: 6 }}>
                    <span className="dim" style={{ fontSize: 11.5 }}>subscription_name</span>
                    <input className="input mono" value={subscriptionName} onChange={(e) => setSubscriptionName(e.target.value)} placeholder="auditai_sg_sub" required />
                  </label>
                  <label className="stack" style={{ gap: 6 }}>
                    <span className="dim" style={{ fontSize: 11.5 }}>slot_name</span>
                    <input className="input mono" value={slotName} onChange={(e) => setSlotName(e.target.value)} placeholder="auditai_sg_slot" required />
                  </label>
                </div>
                <div className="grid-2" style={{ gap: 12 }}>
                  <label className="stack" style={{ gap: 6 }}>
                    <span className="dim" style={{ fontSize: 11.5 }}>ภูมิภาคปลายทาง</span>
                    <input className="input mono" value={region} onChange={(e) => setRegion(e.target.value)} placeholder="ap-southeast-1" required />
                  </label>
                  <label className="stack" style={{ gap: 6 }}>
                    <span className="dim" style={{ fontSize: 11.5 }}>โฮสต์</span>
                    <input className="input mono" value={host} onChange={(e) => setHost(e.target.value)} placeholder="10.30.0.12" required />
                  </label>
                </div>
                <div className="grid-2" style={{ gap: 12 }}>
                  <label className="stack" style={{ gap: 6 }}>
                    <span className="dim" style={{ fontSize: 11.5 }}>พอร์ต</span>
                    <input className="input mono" value={port} onChange={(e) => setPort(e.target.value)} inputMode="numeric" />
                  </label>
                  <label className="stack" style={{ gap: 6 }}>
                    <span className="dim" style={{ fontSize: 11.5 }}>ฐานข้อมูลปลายทาง</span>
                    <input className="input mono" value={databaseName} onChange={(e) => setDatabaseName(e.target.value)} required />
                  </label>
                </div>
                <div className="grid-2" style={{ gap: 12 }}>
                  <label className="stack" style={{ gap: 6 }}>
                    <span className="dim" style={{ fontSize: 11.5 }}>นโยบายข้อขัดแย้ง</span>
                    <select className="input" value={conflictPolicy} onChange={(e) => setConflictPolicy(e.target.value as typeof conflictPolicy)}>
                      <option value="halt">หยุดและให้คนตรวจ</option>
                      <option value="retry">ลองใหม่แบบถอยหลัง</option>
                      <option value="skip_transaction">ข้ามธุรกรรมที่ผิดพลาด</option>
                      <option value="pause_and_review">หยุดชั่วคราวเพื่อสอบสวน</option>
                    </select>
                  </label>
                  <label className="stack" style={{ gap: 6 }}>
                    <span className="dim" style={{ fontSize: 11.5 }}>งบความล่าช้า (วินาที)</span>
                    <input className="input mono" value={maxLagSeconds} onChange={(e) => setMaxLagSeconds(e.target.value)} inputMode="numeric" />
                  </label>
                </div>
                <p className="dim" style={{ fontSize: 11.5, margin: 0 }}>
                  การลงทะเบียนนี้บันทึกเจตนาและการวัด ยังไม่รัน CREATE SUBSCRIPTION บนเครื่องปลายทาง เพราะต้องใช้ข้อมูลเชื่อมต่อที่ API ไม่เก็บไว้
                </p>
                <button className="btn btn-primary" type="submit" disabled={createSubscription.isPending}>
                  {createSubscription.isPending ? 'กำลังบันทึก…' : 'ลงทะเบียน subscription'}
                </button>
              </form>
            ) : null}

            <div className="card card-pad">
              <span className="card-label">กติกาที่ใช้ตัดสิน</span>
              <div className="stack" style={{ gap: 8, marginTop: 8, fontSize: 12 }}>
                <div className="dim">ไบต์ค้าง = pg_current_wal_lsn − ตำแหน่งที่ผู้รับได้รับหรือเล่นแล้ว</div>
                <div className="dim">สถานะ streaming ต้องมี worker ทำงานครบทุกตารางและอ่าน LSN ได้ทั้งสองฝั่ง</div>
                <div className="dim">สล็อตที่ไม่ได้ใช้งานจะหน่วง WAL ตั้งแต่ restart_lsn และอาจทำให้ดิสก์ผู้เผยแพร่เต็ม</div>
                <div className="dim">นโยบาย halt ไม่ใช่การแก้ข้อขัดแย้ง แต่คือการหยุดรอคนตรวจก่อนข้อมูลสองฝั่งต่างกันมากขึ้น</div>
                {(data?.notes ?? []).map((note) => (
                  <div className="dim" key={note}>{note}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function sevClass(tone: string): string {
  return tone === 'info' ? 'sev dim' : `sev sev-${tone}`
}

function stateTone(state: ObservedState): string {
  switch (state) {
    case 'streaming':
      return 'low'
    case 'lagging':
      return 'high'
    case 'error':
      return 'critical'
    case 'copying':
    case 'creating':
      return 'medium'
    default:
      return 'info'
  }
}

function slotTone(risk: SlotRisk): string {
  switch (risk) {
    case 'ok':
      return 'low'
    case 'watch':
      return 'medium'
    case 'high':
      return 'high'
    case 'critical':
      return 'critical'
    default:
      return 'info'
  }
}

function bytesText(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 ไบต์'
  if (bytes < 1024) return `${number(bytes)} ไบต์`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}
